import { GlobalConfigType } from 'actions/global.config.type'
import cloneDeep from 'clone-deep'
import { createUserAdditionalInputs as defaultCreateUserInputs } from 'components/Modals/CreateUser/constants'
import {
  FieldOrigin,
  fieldOriginForVisibility,
  fieldOriginOptions,
  ILoanFieldPos,
  isCustomOrigin,
  IVisibleProp,
  LoanFieldProps,
} from 'config/loan.input.visibility.type'
import { accountTypeWithBorrower } from 'config/user.type.constants'
import { defaultInputs as defaultBorrowerInformationInputs } from 'pages/LoanApplication/BorrowerInformation/constants'
import { defaultInputs as defaultDeclarationsHmdaInputs } from 'pages/LoanApplication/DeclarationsHmda/constants'
import { defaultInputs as defaultPropertyInformationInputs } from 'pages/LoanApplication/PropertyInformation/constants'
import { store } from 'reducers'
import { loanTypeMap, partiesMap } from 'reducers/loanDetail.reducer'
import { capitalizeFirstLetter, combineObjectsDeep, fillObject } from 'utils'

import { InputType } from './input.type.constants'
import { getInitialLoanFields } from './loan.constants'

export enum CustomFieldType {
  Text = 'text',
  Number = 'number',
  ThousandSepWithDoller = 'dollar',
  Percentage = 'percentage',
  Email = 'email',
  Phone = 'phone',
  Ssn = 'ssn',
  Mers = 'mers',
  Date = 'date',
  Select = 'select',
  Toggle = 'toggle',
  TextArea = 'textarea',
  URL = 'url',
}

export const CustomFieldTypeOptions: Record<CustomFieldType, string> = {
  [CustomFieldType.Text]: 'Text',
  [CustomFieldType.Number]: 'Number',
  [CustomFieldType.ThousandSepWithDoller]: 'Dollar',
  [CustomFieldType.Percentage]: 'Percentage',
  [CustomFieldType.Email]: 'Email',
  [CustomFieldType.Phone]: 'Phone',
  [CustomFieldType.Ssn]: 'SSN',
  [CustomFieldType.Mers]: 'MERS',
  [CustomFieldType.Date]: 'Date',
  [CustomFieldType.Select]: 'Select',
  [CustomFieldType.Toggle]: 'Toggle',
  [CustomFieldType.TextArea]: 'TextArea',
  [CustomFieldType.URL]: 'URL',
}

export const defaultOtherInputs = (): Record<string, InputType> => {
  const partyKeys = Object.keys(partiesMap).filter((v) => v != 'creator')

  const inputs = fillObject(partyKeys, (key: string) => ({
    title: partiesMap[key],
    inputType: 'select',
    hasDefaultOption: true,
    options: {},
    error: '',
    required: true,
    sort: false,
  }))

  // Add custom "How Did You Hear About Us?" field
  inputs.howDidYouHearAboutUs = {
    title: 'How Did You Hear About Us?',
    inputType: 'select',
    hasDefaultOption: true,
    options: {},
    error: '',
    required: true,
    sort: false,
  }

  return inputs
}

export const isAccountControlField = (fieldOrigin: FieldOrigin, fieldKey: string) =>
  fieldOrigin == FieldOrigin.Other && Object.keys(partiesMap).includes(fieldKey) && fieldKey != 'creator'

export const isUtmCampaignField = (fieldOrigin: FieldOrigin, fieldKey: string) =>
  fieldOrigin == FieldOrigin.Other && fieldKey === 'howDidYouHearAboutUs'

export type InputTypeOfLoans = Record<FieldOrigin, Record<string, InputType>>

export const getFieldOriginOptions = (withCustom: boolean = true) => {
  const { globalConfigReducer } = store.getState()
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  return {
    ...fieldOriginOptions,
    ...(withCustom ? loanFieldProperties.customTitle : {}),
  }
}

export const getLoanInputFields = ({
  other = false,
  custom = false,
}: {
  other?: boolean
  custom?: boolean
} = {}): InputTypeOfLoans => {
  const loanStructureFields = getInitialLoanFields()
  const { globalConfigReducer } = store.getState()
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = loanFieldProperties[FieldOrigin.LoanStructure]

  fieldProps &&
    Object.keys(fieldProps).map((key) => {
      if (!isCustomFieldKey(key)) return
      loanStructureFields[key] = cloneDeep(fieldProps[key] as any)
    })

  const data = {
    [FieldOrigin.LoanStructure]: {
      ...loanStructureFields,
      LTV: {
        title: 'LTV',
        inputType: 'text',
        type: 'number',
        error: '',
        prefix: '%',
      },
      ARV_LTV: {
        title: 'ARV-LTV',
        inputType: 'text',
        type: 'number',
        error: '',
        prefix: '%',
      },
    },
    [FieldOrigin.BorrowerInformation]: defaultBorrowerInformationInputs(),
    [FieldOrigin.PropertyInformation]: defaultPropertyInformationInputs(),
    // [FieldOrigin.AssetLiability]: {},
    // [FieldOrigin.TrackRecord]: {},
    [FieldOrigin.DeclarationsHMDA]: defaultDeclarationsHmdaInputs(),
  } as any
  if (other) data[FieldOrigin.Other] = defaultOtherInputs()
  if (custom) {
    data[FieldOrigin.Custom] = {}
    Object.keys(loanFieldProperties).forEach((fieldOrigin) => {
      if (isCustomOrigin(fieldOrigin)) data[fieldOrigin] = loanFieldProperties[fieldOrigin]
    })
  }

  data[FieldOrigin.ManageAccount] = fillObject(['warehouseApproved'], (key: string) => defaultCreateUserInputs()[key])

  return data
}

export const getVisibleLogics2Text = (
  visibleProps: IVisibleProp,
  _fieldOriginOptions: Record<FieldOrigin, string> | null = null,
  _inputFields: InputTypeOfLoans | null = null,
) => {
  const { title, visibleLogic, visibleAccountTypes, visibleLoanTypes } = visibleProps
  let newTexts = []
  if (title) newTexts.push(`Title: ${title}`)
  if (visibleLogic) {
    const inputFields = combineObjectsDeep(getLoanInputFields(), _inputFields || {})

    const visibleText = visibleLogic
      .map((logic) =>
        logic
          .filter((l) => l.fieldOrigin && l.fieldKey)
          .map((l) => {
            const { fieldOrigin, fieldKey } = l
            const input = inputFields[fieldOrigin]?.[fieldKey]
            if (!input) return ''
            const { inputType } = input
            let val = l.value
            if ((inputType == 'select' || inputType == 'Select') && input.options && !Array.isArray(input.options)) {
              val = input.options[val as string]
            }
            return `${_fieldOriginOptions?.[fieldOrigin] || fieldOriginOptions[fieldOrigin] || 'Custom'} -> ${
              input.title
            } = ${val}`
          })
          .join('\n'),
      )
      .filter((v) => !!v)
      .join('\n\n')

    newTexts.push(visibleText)
  }
  if (visibleAccountTypes) {
    const invisibleTypes: string[] = []
    Object.keys(accountTypeWithBorrower()).forEach((accountType) => {
      if (visibleAccountTypes[accountType] === false) invisibleTypes.push(accountType)
    })
    if (invisibleTypes.length) {
      const text = invisibleTypes.map((type) => accountTypeWithBorrower()[type]).join(', ')
      newTexts.push(`Invisible for: ${text}`)
    }
  }
  if (visibleLoanTypes) {
    const invisibleTypes: string[] = []
    Object.keys(loanTypeMap).forEach((loanType) => {
      if (visibleLoanTypes[loanType] === false) invisibleTypes.push(loanType)
    })
    if (invisibleTypes.length) {
      const text = invisibleTypes.map((type) => loanTypeMap[type]).join(', ')
      newTexts.push(`Invisible for: ${text}`)
    }
  }

  return newTexts.join('\n\n')
}

// Filter by inputType
export const getLoanInputFieldOptions = (
  inputType = '',
  { ignoredFields = [] }: { ignoredFields?: ILoanFieldPos[] } = {},
  _inputFields: InputTypeOfLoans | null = null,
  inputFieldTitles: Record<string, { title: string; fields: Record<string, string> }> | null = null,
) => {
  const defaultInputFields = getLoanInputFields({ other: true, custom: true })
  const inputFields = _inputFields || defaultInputFields
  const options: Record<FieldOrigin, Record<string, string>> = {} as any

  fieldOriginForVisibility.forEach((fieldOrigin) => {
    if (inputFields[fieldOrigin]) return
    inputFields[fieldOrigin] = defaultInputFields[fieldOrigin]
  })

  Object.keys(inputFields).forEach((_fieldOrigin) => {
    const fieldOrigin = _fieldOrigin as any as FieldOrigin
    const inputs = inputFields[fieldOrigin]
    options[fieldOrigin] = {}

    Object.keys(inputs).forEach((key) => {
      const input = inputs[key]
      if (['section', 'Section'].includes(input.inputType)) return
      if (['GoogleMap', 'googleMap'].includes(input.inputType)) return
      const isIgnoredField = ignoredFields.findIndex((v) => v.fieldOrigin == fieldOrigin && v.fieldKey == key) != -1
      if (isIgnoredField) return
      if (inputType && input.inputType && input.inputType?.toLowerCase() != inputType.toLowerCase()) return
      if (input.disabled) return

      let title = input.title
      if (inputFieldTitles) title = inputFieldTitles?.[fieldOrigin]?.fields?.[key] || title

      if (inputType) options[fieldOrigin][key] = title
      else options[fieldOrigin][key] = `${title} [${capitalizeFirstLetter(input.inputType || 'Text')}]`
    })
  })

  return options
}

export const customFieldType2Input = (fieldType: CustomFieldType) => {
  let input: InputType

  switch (fieldType) {
    case CustomFieldType.Text:
    case CustomFieldType.Date:
    case CustomFieldType.Number:
    case CustomFieldType.ThousandSepWithDoller:
    case CustomFieldType.Percentage:
    case CustomFieldType.Email:
    case CustomFieldType.Phone:
    case CustomFieldType.Ssn:
    case CustomFieldType.URL:
      const types = {
        [CustomFieldType.Text]: 'text',
        [CustomFieldType.Date]: 'date',
        [CustomFieldType.Number]: 'number',
        [CustomFieldType.ThousandSepWithDoller]: 'thousandSep',
        [CustomFieldType.Percentage]: 'number',
        [CustomFieldType.Email]: 'email',
        [CustomFieldType.Phone]: 'phone',
        [CustomFieldType.Ssn]: 'ssn',
        [CustomFieldType.Mers]: 'mers',
        [CustomFieldType.URL]: 'url',
      }
      input = {
        inputType: 'text',
        type: types[fieldType] as any,
        title: '',
      }
      if (fieldType == CustomFieldType.ThousandSepWithDoller) input.prefix = '$'
      if (fieldType == CustomFieldType.Percentage) input.prefix = '%'
      break
    case CustomFieldType.Toggle:
      input = {
        inputType: 'ToggleButton',
        title: '',
      }
      break
    case CustomFieldType.Select:
      input = {
        inputType: 'select',
        options: [],
        title: '',
        hasDefaultOption: true,
      }
      break
    case CustomFieldType.TextArea:
      input = {
        inputType: 'TextArea',
        title: '',
      }
      break
    default:
      input = {
        inputType: 'text',
        title: '',
      }
  }
  input.required = true
  return input
}

export const loanOriginsWithSections: FieldOrigin[] = [
  FieldOrigin.LoanStructure,
  FieldOrigin.BorrowerInformation,
  FieldOrigin.PropertyInformation,
  FieldOrigin.AssetLiability,
  FieldOrigin.DeclarationsHMDA,
  // FieldOrigin.CustomInformation,
]

export const canHaveSection = (fieldOrigin: FieldOrigin) =>
  loanOriginsWithSections.includes(fieldOrigin) || isCustomOrigin(fieldOrigin)

export const canAddNewField = (fieldOrigin: FieldOrigin) =>
  isCustomOrigin(fieldOrigin) ||
  [
    FieldOrigin.LoanStructure,
    FieldOrigin.BorrowerInformation,
    FieldOrigin.PropertyInformation,
    FieldOrigin.DeclarationsHMDA,
  ].includes(fieldOrigin)

export const isCustomFieldKey = (key: string) => key == `${Number(key)}` && `${key}`.length == 13

export const isCustomFieldKeys = (keys: string[]) => !keys.find((key) => !isCustomFieldKey(key))
